/**
 * Kalender — Top-Level-Page (Welle-3-Modul, 2026-06-30, Cluster 17.2).
 *
 * Aktueller Stand (2026-07-03):
 * - Month + Week + Day-View (View-Switcher), Range-Load je nach View
 * - Aggregat-Events aus 5 Quellen: personal, tasks, absences, todos, fuhrpark
 * - Personal-Event-Create per „+"-Knopf
 * - Klick auf Event → Read-Only-Quelle navigiert zu Quell-Modul,
 *   Personal-Event oeffnet Edit-Modal
 * - Scopes-Filter (5 Toggle-Chips) — welche Quellen anzeigen. Zustand in
 *   localStorage persistiert, wird als `?scopes=...` an /aggregate gehaengt.
 * - iCal-Subscribe (⚙-Button in der Header-Leiste) — Modal mit
 *   KalenderSubscribeCard fuer .ics-Token-Verwaltung
 *
 * Dokument-Navigation (Jörg 2026-09-29, Abhakliste 2.90 + 2.97, 2026-10-01):
 * - Ansicht, Tag, Monatsblatt und Quellen stehen ersetzend in der Adresse
 *   (`view`, `date`, `monat`, `quellen`) — „Zurück" aus einer Aufgabe trifft
 *   dieselbe Kalenderansicht; `state.from` entfällt (useBack geht im Verlauf).
 * - Termin bearbeiten = Dokument-Dialog: Anzeige zuerst, „Bearbeiten" schaltet um,
 *   Löschen mit Rückfrage im Portal-Layout. Neuer Termin: Dialog-Schutz.
 *
 * Out-of-Scope (Folge-Wellen):
 * - Recurrence-Rules (wiederkehrende Termine mit RRULE)
 * - Weitere Cross-Modul-Quellen bei Bedarf (Bibliothek-Ablaeufe,
 *   Service-Desk-Termine, Chat-erwaehnte Termine)
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, ChevronLeft, ChevronRight, X, Loader2, Calendar as CalIcon, Settings2, Play, Square, Bell } from 'lucide-react';
import { api, apiFetch } from '@onvice/portal-shell-api';
import KalenderSubscribeCard from '../components/KalenderSubscribeCard';
import {
  RecurrenceEditor, DocumentDialog, GuardedModal, useDirtySnapshot, useDialogs,
  useListState, param, useScrollRestore, usePageLabel,
} from '@onvice/portal-ui';
import { useFederatedModule } from '../lib/useFederatedModule';

// switchTimer dynamisch aus bauberichteV2 — statischer Import wuerde den
// Federation-Bootstrap-Preload zwingen und einen bauberichteV2-Ausfall zum
// globalen Portal-Boot-Kill machen. Regel:
// [[feedback_federation_no_static_imports]].
type BauberichteV2Public = {
  switchTimer: (opts: { taskId: string | null }) => Promise<unknown>;
};

/** Aktiver Timer-Snapshot vom BV2-Backend — leichter Ausschnitt. */
interface ActiveTimerLite { taskId: string | null }

type ViewMode = 'month' | 'week' | 'day' | 'list';
type ScopeKey = 'personal' | 'tasks' | 'absences' | 'todos' | 'fuhrpark' | 'ressourcen' | 'holidays';

/**
 * Scopes mit Anzeige-Metadaten. `key` matched das Backend
 * (`kalender/routes.ts` `SCOPE_DEFAULTS`). `color` MUSS mit der Event-Farbe
 * im Backend uebereinstimmen (`kalender/routes.ts` loadPersonal/loadTasks/...) —
 * sonst zeigen Chip und Event-Bullets unterschiedliche Farben und der User
 * kann Filter+Grid nicht mehr optisch verknuepfen.
 */
const ALL_SCOPES: Array<{ key: ScopeKey; label: string; color: string }> = [
  { key: 'personal', label: 'Eigene Termine', color: '#3b82f6' },  // blue-500
  { key: 'tasks',    label: 'Aufgaben',        color: '#f59e0b' },  // amber-500
  { key: 'absences', label: 'Abwesenheiten',   color: '#ef4444' },  // red-500
  { key: 'todos',    label: 'Todos',           color: '#10b981' },  // emerald-500
  { key: 'fuhrpark', label: 'Fuhrpark',        color: '#a855f7' },  // purple-500
  { key: 'ressourcen', label: 'Ressourcen',    color: '#0891b2' },  // cyan-600 (Wartungen)
  { key: 'holidays', label: 'Feiertage',       color: '#eab308' },  // amber-500 (Highlight)
];
const SCOPES_STORAGE_KEY = 'kalender.visibleScopes';
const DEFAULT_SCOPES: ScopeKey[] = ALL_SCOPES.map((s) => s.key);

function loadVisibleScopes(): ScopeKey[] {
  if (typeof window === 'undefined') return DEFAULT_SCOPES;
  try {
    const raw = localStorage.getItem(SCOPES_STORAGE_KEY);
    if (!raw) return DEFAULT_SCOPES;
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return DEFAULT_SCOPES;
    const valid = parsed.filter((v): v is ScopeKey =>
      typeof v === 'string' && DEFAULT_SCOPES.includes(v as ScopeKey),
    );
    // Wenn alle Scopes weg-geschaltet waeren → als Default alle anzeigen
    // (User verliert sonst versehentlich den Kalender komplett).
    return valid.length > 0 ? valid : DEFAULT_SCOPES;
  } catch {
    return DEFAULT_SCOPES;
  }
}
function persistVisibleScopes(scopes: ScopeKey[]): void {
  try { localStorage.setItem(SCOPES_STORAGE_KEY, JSON.stringify(scopes)); } catch { /* ignore */ }
}
function parseScopes(raw: string): ScopeKey[] {
  const valid = raw.split(',').map((v) => v.trim()).filter((v): v is ScopeKey => DEFAULT_SCOPES.includes(v as ScopeKey));
  return valid.length > 0 ? valid : DEFAULT_SCOPES;
}

/**
 * Listenzustand in der Adresse (Regel „Dokument-Navigation" R2, Abhakliste 2.90): Ansicht,
 * gewählter Tag, Monatsblatt und sichtbare Quellen — immer ersetzend geschrieben. `date`
 * leer = heute; `monat` nur, wenn das Monatsblatt vom gewählten Tag abweicht; `quellen`
 * leer = die auf dem Gerät gemerkte Auswahl.
 */
const KALENDER_LIST = {
  view: param.oneOf(['month', 'week', 'day', 'list'] as const, 'month'),
  date: param.date(),
  monat: param.month(),
  quellen: param.string(),
};

function parseDay(raw: string): Date | null {
  if (!raw) return null;
  const d = new Date(`${raw}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : stripTime(d);
}
function isoMonth(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

interface AggregatedEvent {
  id: string;
  source: 'personal' | 'task' | 'absence' | 'todo' | 'fuhrpark' | 'ressourcen' | 'holiday';
  source_module: string;
  source_id: string;
  is_recurring_instance?: boolean;
  title: string;
  start_at: string;
  end_at: string | null;
  all_day: boolean;
  location: string | null;
  link_url: string;
  color: string;
  read_only: boolean;
}

export default function KalenderPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [list, setList] = useListState(KALENDER_LIST);
  const dlg = useDialogs();
  usePageLabel('Kalender', 'list');
  const bv2 = useFederatedModule<BauberichteV2Public>('bauberichteV2/public');
  const switchTimer = bv2?.switchTimer ?? null;
  // Abhakliste 2.97: kein `state.from` mehr — die Detailseite geht mit useBack im
  // Verlauf zurück und trifft diese Adresse samt Ansicht, Tag und Quellen.
  const goToEvent = useCallback((url: string) => {
    navigate(url);
  }, [navigate]);
  // SD-2026-00034 (Onvice, 2026-07-31): Zentraler Klick-Handler statt inline
  // `if (e.read_only) goToEvent(...)`. Regeln:
  //  - Feiertag: nichts tun (link_url ist nur `/kalender` ohne Tag — Sprung
  //    wuerde Kalender auf Heute zuruecksetzen, User verliert Kontext).
  //  - Personal-Termin: Edit-Modal oeffnen (bearbeitbar + loeschbar).
  //  - Alle anderen read_only-Quellen (Task/Absence/Todo/Fuhrpark/Ressourcen):
  //    zu Quell-Modul navigieren wie bisher.
  const handleEventClick = useCallback((e: AggregatedEvent) => {
    if (e.source === 'holiday') return;
    if (e.source === 'personal') { setEditingEventId(e.source_id); return; }
    if (e.read_only) goToEvent(e.link_url);
  }, [goToEvent]);
  // Phase 6 (2026-06-30): Mobile-Default ist Day-View — Month-Grid ist auf
  // kleinen Screens unbedienbar. URL-Param hat Vorrang, damit der User das
  // explizit ueberschreiben kann.
  // Ansicht ohne Angabe auf dem Handy = Tag (Month-Grid ist dort unbedienbar). Nur beim
  // ersten Aufruf ohne `view` in der Adresse, ersetzend.
  useEffect(() => {
    if (searchParams.get('view')) return;
    if (typeof window === 'undefined') return;
    if (window.matchMedia?.('(max-width: 768px)').matches) setList({ view: 'day' });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const view: ViewMode = list.view;
  const setView = (v: ViewMode): void => { setList({ view: v }); };
  // Portalweite Suche (PS-4, 2026-09-09): /kalender?date=YYYY-MM-DD springt direkt zum
  // Monat/Tag des Treffers statt auf Heute — seit 2.90 ist `date` zugleich der gewählte Tag.
  const today = useMemo(() => stripTime(new Date()), []);
  const selectedDay: Date = useMemo(() => parseDay(list.date) ?? today, [list.date, today]);
  const cursor: Date = useMemo(() => {
    const m = list.monat ? parseDay(`${list.monat}-01`) : null;
    return firstOfMonth(m ?? selectedDay);
  }, [list.monat, selectedDay]);
  const setSelectedDay = useCallback((d: Date | null): void => {
    setList({ date: d ? isoDay(stripTime(d)) : '' });
  }, [setList]);
  const [events, setEvents] = useState<AggregatedEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  useScrollRestore(!loading);
  // ?neu=1 (Tiefenlink Test-Cockpit, 2026-09-27): Termin-Dialog sofort öffnen.
  const [newOpen, setNewOpen] = useState(() => searchParams.get('neu') === '1');
  const [subscribeOpen, setSubscribeOpen] = useState(false);
  // SD-2026-00034 (Onvice, 2026-07-31): Klick auf Personal-Termin oeffnet
  // Edit-Modal (vorher: Klick auf non-read_only-Event tat nichts).
  const [editingEventId, setEditingEventId] = useState<string | null>(null);
  // Scopes-Filter (2026-07-03): welche Quellen im Grid gezeigt werden. Gemerkt auf dem
  // Gerät (localStorage); steht `quellen` in der Adresse, gilt die Adresse (2.90) — so trifft
  // „Zurück" dieselbe Auswahl. Wird als ?scopes=X,Y an /aggregate gehaengt.
  const [storedScopes, setStoredScopes] = useState<ScopeKey[]>(() => loadVisibleScopes());
  const visibleScopes: ScopeKey[] = useMemo(
    () => (list.quellen ? parseScopes(list.quellen) : storedScopes),
    [list.quellen, storedScopes],
  );
  const setScopesAndPersist = useCallback((next: ScopeKey[]): void => {
    setStoredScopes(next);
    persistVisibleScopes(next);
    setList({ quellen: next.join(',') });
  }, [setList]);
  // Aktiver BV2-Timer — fuer Inline-Play/Stop in der Liste. null = kein Timer
  // laeuft. Bei taskId-Match rendern wir Stop, sonst Play. Refresh nach jeder
  // switchTimer-Aktion.
  const [activeTimer, setActiveTimer] = useState<ActiveTimerLite | null>(null);
  const loadActiveTimer = useCallback(async (): Promise<void> => {
    try {
      const r = await apiFetch<{ success: boolean; data: { taskId: string | null } | null }>('/modules/bauberichteV2/timer/active');
      setActiveTimer(r?.data ? { taskId: r.data.taskId } : null);
    } catch { /* fail-soft: Timer-Feature deaktiviert wenn BV2 nicht da */ }
  }, []);
  useEffect(() => { void loadActiveTimer(); }, [loadActiveTimer]);
  const toggleTaskTimer = useCallback(async (taskId: string): Promise<void> => {
    if (!switchTimer) {
      console.warn('[Kalender] bauberichteV2 nicht geladen — Timer-Toggle nicht verfuegbar');
      return;
    }
    const isActive = activeTimer?.taskId === taskId;
    try {
      // switchTimer feuert bei suggestGpsUpdate ein Portal-Event — hier ohne
      // GPS-Capture (Kalender ist nicht das Terrain fuer Standort-Erfassung).
      await switchTimer({ taskId: isActive ? null : taskId });
      await loadActiveTimer();
      // Aggregate braucht nicht neu geladen zu werden — Task-Zeilen aendern
      // sich durch switchTimer nicht (nur time_entries wachsen). Der Marker
      // im Grid haengt an activeTimer, wird durch loadActiveTimer aktualisiert.
    } catch (err) {
      console.warn('[Kalender] Timer-Switch fehlgeschlagen:', (err as Error).message);
    }
  }, [activeTimer?.taskId, loadActiveTimer, switchTimer]);

  const toggleScope = useCallback((key: ScopeKey): void => {
    const next = visibleScopes.includes(key)
      ? visibleScopes.filter((s) => s !== key)
      : [...visibleScopes, key];
    // Nie alle deaktivieren — sonst leerer Kalender ohne Recovery
    if (next.length === 0) return;
    setScopesAndPersist(next);
  }, [visibleScopes, setScopesAndPersist]);

  const monthRange = useMemo(() => {
    // Lade je nach View — fuer Month grosse Range, fuer Week +-1 Woche, fuer Day +-1 Tag.
    const anchor = view === 'month' ? firstOfMonth(cursor) : selectedDay;
    const from = new Date(anchor);
    const to = new Date(anchor);
    if (view === 'month') {
      from.setDate(from.getDate() - 7);
      to.setMonth(to.getMonth() + 1);
      to.setDate(to.getDate() + 7);
    } else if (view === 'week') {
      from.setDate(from.getDate() - 7);
      to.setDate(to.getDate() + 14);
    } else if (view === 'list') {
      // Liste: 7 Tage zurueck, 90 Tage vorwaerts — analog BV2-Kalender.
      from.setDate(from.getDate() - 7);
      to.setDate(to.getDate() + 90);
    } else {
      from.setDate(from.getDate() - 1);
      to.setDate(to.getDate() + 1);
    }
    from.setHours(0, 0, 0, 0);
    to.setHours(23, 59, 59, 999);
    return { from: from.toISOString(), to: to.toISOString() };
  }, [cursor, view, selectedDay]);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError('');
    try {
      const scopesParam = visibleScopes.join(',');
      const r = await api.get<{ success: boolean; events: AggregatedEvent[] }>(
        `/kalender/aggregate?from=${encodeURIComponent(monthRange.from)}&to=${encodeURIComponent(monthRange.to)}&scopes=${encodeURIComponent(scopesParam)}`,
      );
      setEvents(r.events ?? []);
    } catch (err) {
      // PWA-Offline (Jörg-Fund 2026-09-09): Netzfehler nicht als rohes
      // „Failed to fetch" zeigen — der Read-Cache liefert bereits den letzten
      // Stand des Zeitraums; ohne Cache-Treffer gibt es einen deutschen
      // Hinweis statt Entwickler-Jargon.
      const isNetErr = err instanceof TypeError
        || (err instanceof Error && !('status' in err) && /fetch|network|load failed/i.test(err.message));
      setError(isNetErr
        ? 'Offline — dieser Zeitraum wurde noch nicht geladen. Angezeigt wird der zuletzt bekannte Stand.'
        : (err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [monthRange, visibleScopes]);

  useEffect(() => { void load(); }, [load]);

  const eventsByDay = useMemo(() => {
    const map = new Map<string, AggregatedEvent[]>();
    for (const e of events) {
      const start = stripTime(new Date(e.start_at));
      const end = e.end_at ? stripTime(new Date(e.end_at)) : start;
      // Multi-Day-Event: pro Tag im Bereich eintragen.
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const key = isoDay(d);
        const list = map.get(key) ?? [];
        list.push(e);
        map.set(key, list);
      }
    }
    return map;
  }, [events]);

  const selectedDayEvents = eventsByDay.get(isoDay(selectedDay)) ?? [];
  const weekDays = useMemo(() => weekFromAnchor(selectedDay), [selectedDay]);

  const shiftDays = (delta: number): void => {
    const next = new Date(selectedDay);
    next.setDate(next.getDate() + delta);
    setList({ date: isoDay(stripTime(next)), monat: '' });
  };
  const shiftMonth = (delta: number): void => {
    const next = addMonths(cursor, delta);
    // Monatsblatt gleich dem Monat des gewählten Tags → Parameter entfällt.
    setList({ monat: isoMonth(next) === isoMonth(selectedDay) ? '' : isoMonth(next) });
  };
  const goPrev = (): void => {
    if (view === 'month') shiftMonth(-1);
    else if (view === 'week') shiftDays(-7);
    else shiftDays(-1);
  };
  const goNext = (): void => {
    if (view === 'month') shiftMonth(1);
    else if (view === 'week') shiftDays(7);
    else shiftDays(1);
  };
  const goToday = (): void => { setList({ date: '', monat: '' }); };

  const periodLabel = view === 'month' ? formatMonth(cursor)
    : view === 'week'  ? formatWeekLabel(weekDays)
    : view === 'list'  ? 'Liste (–7 / +90 Tage)'
    : formatDayHeader(selectedDay);

  return (
    <div className="max-w-6xl mx-auto px-4 py-6" data-test-anchor="list" data-view={view}>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <CalIcon size={22} /> Kalender
        </h1>
        <div className="flex items-center gap-2 flex-wrap">
          {/* View-Switcher */}
          <div className="flex items-center gap-0.5 p-0.5 rounded-md bg-muted">
            {(['month', 'week', 'day', 'list'] as const).map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setView(v)}
                className={`px-3 py-1.5 text-sm rounded transition-colors ${
                  view === v ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {v === 'month' ? 'Monat' : v === 'week' ? 'Woche' : v === 'day' ? 'Tag' : 'Liste'}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={goPrev}
            className="p-2 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground"
            title="Zurueck"
          >
            <ChevronLeft size={18} />
          </button>
          <span className="font-medium min-w-[12rem] text-center text-sm">{periodLabel}</span>
          <button
            type="button"
            onClick={goNext}
            className="p-2 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground"
            title="Vorwaerts"
          >
            <ChevronRight size={18} />
          </button>
          <button
            type="button"
            onClick={goToday}
            className="px-3 py-1.5 rounded-md text-sm text-muted-foreground hover:text-foreground"
          >
            Heute
          </button>
          <button
            type="button"
            onClick={() => setSubscribeOpen(true)}
            className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted"
            title="Kalender-Abos verwalten (iCal-Feeds)"
          >
            <Settings2 size={16} />
          </button>
          <button
            type="button"
            onClick={() => setNewOpen(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90"
          >
            <Plus size={16} /> Termin
          </button>
        </div>
      </div>

      {/* Scopes-Filter — als Chip-Row unter der Header-Leiste. Zeigt/versteckt
          welche der 5 Aggregat-Quellen im Grid erscheinen. Farbpunkt matcht
          die Farbrichtung im Grid (Event-Farbe kommt weiter vom Backend). */}
      <div className="mb-3 flex items-center gap-1.5 flex-wrap">
        <span className="text-xs text-muted-foreground mr-1">Anzeigen:</span>
        {ALL_SCOPES.map((s) => {
          const active = visibleScopes.includes(s.key);
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => toggleScope(s.key)}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs transition-colors ${
                active
                  ? 'bg-primary/10 text-foreground border border-primary/40'
                  : 'bg-muted text-muted-foreground border border-transparent hover:text-foreground'
              }`}
              title={active ? `„${s.label}" ausblenden` : `„${s.label}" einblenden`}
            >
              <span
                className="w-2 h-2 rounded-full flex-shrink-0"
                style={{ backgroundColor: active ? s.color : 'transparent', border: active ? 'none' : `1px solid ${s.color}` }}
              />
              {s.label}
            </button>
          );
        })}
      </div>

      {error && <div className="mb-3 px-3 py-2 rounded-md bg-danger/10 text-danger text-sm">{error}</div>}

      {view === 'month' && (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4">
          <div className="rounded-lg border border-default bg-card overflow-hidden">
            <div className="grid grid-cols-7 bg-muted text-xs font-semibold text-muted-foreground">
              {['Mo','Di','Mi','Do','Fr','Sa','So'].map((d) => (
                <div key={d} className="px-2 py-1.5 text-center">{d}</div>
              ))}
            </div>
            {loading ? (
              <div className="flex items-center gap-2 text-muted-foreground py-12 justify-center">
                <Loader2 size={16} className="animate-spin" /> Laedt...
              </div>
            ) : (
              <MonthGrid
                cursor={cursor}
                eventsByDay={eventsByDay}
                selectedDay={selectedDay}
                onSelectDay={setSelectedDay}
              />
            )}
          </div>

          <aside className="rounded-lg border border-default bg-card p-3">
            <div className="text-sm font-semibold mb-2">
              {formatDayHeader(selectedDay)}
            </div>
            <DayEventList events={selectedDayEvents} onEventClick={handleEventClick} />
          </aside>
        </div>
      )}

      {view === 'week' && (
        <div className="rounded-lg border border-default bg-card overflow-hidden">
          <div className="grid grid-cols-7 bg-muted text-xs font-semibold text-muted-foreground">
            {weekDays.map((d) => {
              const isToday = isoDay(d) === isoDay(new Date());
              return (
                <button
                  key={isoDay(d)}
                  type="button"
                  onClick={() => setSelectedDay(d)}
                  className={`px-2 py-1.5 text-center hover:bg-card transition-colors ${isToday ? 'text-primary font-bold' : ''}`}
                >
                  {d.toLocaleDateString('de-DE', { weekday: 'short' })} {d.getDate()}.
                </button>
              );
            })}
          </div>
          {loading ? (
            <div className="flex items-center gap-2 text-muted-foreground py-12 justify-center">
              <Loader2 size={16} className="animate-spin" /> Laedt...
            </div>
          ) : (
            <div className="grid grid-cols-7 divide-x divide-default min-h-[400px]">
              {weekDays.map((d) => {
                const key = isoDay(d);
                const list = eventsByDay.get(key) ?? [];
                const isToday = key === isoDay(new Date());
                return (
                  <div key={key} className={`p-1.5 space-y-1 ${isToday ? 'bg-primary/5' : ''}`}>
                    {list.length === 0 && <div className="text-xs text-muted-foreground italic">—</div>}
                    {list.map((e) => (
                      <button
                        key={e.id}
                        type="button"
                        onClick={() => handleEventClick(e)}
                        className="w-full text-left px-1.5 py-1 rounded text-xs hover:opacity-80"
                        style={{ backgroundColor: `${e.color}25`, color: e.color, borderLeft: `3px solid ${e.color}` }}
                        title={e.is_recurring_instance ? `${e.title} (Serie)` : e.title}
                      >
                        <div className="font-medium truncate">{e.is_recurring_instance ? '🔁 ' : ''}{e.title}</div>
                        {!e.all_day && <div className="opacity-70">{formatTime(e.start_at)}</div>}
                      </button>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {view === 'day' && (
        <div
          className="rounded-lg border border-default bg-card p-4 min-h-[400px] touch-pan-y"
          onTouchStart={(e) => {
            const t = e.touches[0];
            if (t) (e.currentTarget as HTMLElement & { _swipeX?: number })._swipeX = t.clientX;
          }}
          onTouchEnd={(e) => {
            const t = e.changedTouches[0];
            const startX = (e.currentTarget as HTMLElement & { _swipeX?: number })._swipeX;
            if (!t || startX == null) return;
            const dx = t.clientX - startX;
            if (Math.abs(dx) < 60) return;
            if (dx < 0) goNext(); else goPrev();
          }}
        >
          <div className="text-sm font-semibold mb-3 text-muted-foreground">
            {formatDayHeader(selectedDay)}
          </div>
          {loading ? (
            <div className="flex items-center gap-2 text-muted-foreground py-12 justify-center">
              <Loader2 size={16} className="animate-spin" /> Laedt...
            </div>
          ) : (
            <DayEventList events={selectedDayEvents} onEventClick={handleEventClick} large />
          )}
        </div>
      )}

      {view === 'list' && (
        <div className="rounded-lg border border-default bg-card overflow-hidden">
          {loading ? (
            <div className="flex items-center gap-2 text-muted-foreground py-12 justify-center">
              <Loader2 size={16} className="animate-spin" /> Laedt...
            </div>
          ) : events.length === 0 ? (
            <div className="text-sm text-muted-foreground italic py-12 text-center">Keine Ereignisse im Zeitraum.</div>
          ) : (
            <ListView
              events={events}
              activeTaskId={activeTimer?.taskId ?? null}
              onToggleTimer={(taskId) => void toggleTaskTimer(taskId)}
              onEventClick={handleEventClick}
            />
          )}
        </div>
      )}

      {newOpen && (
        <NewEventModal
          defaultDay={selectedDay}
          onClose={() => setNewOpen(false)}
          onCreated={() => { setNewOpen(false); void load(); }}
        />
      )}

      {subscribeOpen && (
        <SubscribeModal onClose={() => setSubscribeOpen(false)} />
      )}

      {editingEventId && (
        <EditEventModal
          eventId={editingEventId}
          onClose={() => setEditingEventId(null)}
          onChanged={() => { void load(); }}
          onDeleted={() => { setEditingEventId(null); void load(); }}
          confirmDelete={() => dlg.confirm({ title: 'Termin löschen?', message: 'Der Termin wird endgültig entfernt.', confirmLabel: 'Löschen', danger: true })}
        />
      )}
    </div>
  );
}

/**
 * Modal-Wrapper um die vorhandene <KalenderSubscribeCard/>. Card wird
 * unveraendert eingebettet — Rendering, Fetch, Create, Delete kapselt sie
 * selbst. Hier nur Overlay + Schliess-Kreuz + Klick-outside-to-close.
 */
function SubscribeModal({ onClose }: { onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-[80] flex items-start justify-center bg-black/40 p-4 pt-16 sm:pt-24 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl rounded-lg bg-card border border-default shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-default">
          <h2 className="text-base font-semibold">Kalender-Abos verwalten</h2>
          <button type="button" onClick={onClose} className="text-muted-foreground hover:text-foreground" aria-label="Schliessen">
            <X size={18} />
          </button>
        </div>
        <div className="p-4">
          <KalenderSubscribeCard />
          <div className="mt-3 text-xs text-muted-foreground">
            Ein Abo liefert alle Ereignisse aus den gewaehlten Scopes als .ics-Feed.
            Kopiere die URL in deinen Kalender (Outlook, iCal, Google Kalender)
            als „Kalender abonnieren".
          </div>
        </div>
      </div>
    </div>
  );
}

interface MonthGridProps {
  cursor: Date;
  eventsByDay: Map<string, AggregatedEvent[]>;
  selectedDay: Date | null;
  onSelectDay: (d: Date) => void;
}

function MonthGrid({ cursor, eventsByDay, selectedDay, onSelectDay }: MonthGridProps) {
  const first = firstOfMonth(cursor);
  const startWeekday = (first.getDay() + 6) % 7;  // Mo=0 ... So=6
  const daysInMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
  const totalCells = Math.ceil((startWeekday + daysInMonth) / 7) * 7;
  const today = isoDay(new Date());
  const selectedKey = selectedDay ? isoDay(selectedDay) : null;

  const cells: { day: Date; inMonth: boolean }[] = [];
  for (let i = 0; i < totalCells; i += 1) {
    const d = new Date(first);
    d.setDate(d.getDate() + (i - startWeekday));
    cells.push({ day: d, inMonth: d.getMonth() === cursor.getMonth() });
  }

  return (
    <div className="grid grid-cols-7">
      {cells.map(({ day, inMonth }, i) => {
        const key = isoDay(day);
        const list = eventsByDay.get(key) ?? [];
        const isToday = key === today;
        const isSelected = selectedKey === key;
        return (
          <button
            type="button"
            key={i}
            onClick={() => onSelectDay(day)}
            className={`min-h-[80px] border-t border-l border-default p-1.5 text-left flex flex-col gap-0.5 transition-colors ${
              isSelected ? 'bg-primary/10 ring-1 ring-primary/40 ring-inset' :
              isToday    ? 'bg-primary/5' :
              inMonth    ? 'hover:bg-muted/50' :
                           'bg-muted/30 text-muted-foreground'
            }`}
          >
            <div className={`text-xs font-medium ${isToday ? 'text-primary' : ''}`}>{day.getDate()}</div>
            <div className="flex flex-col gap-0.5 overflow-hidden">
              {list.slice(0, 3).map((e) => (
                <div
                  key={e.id}
                  className="text-[10px] leading-tight truncate flex items-center gap-1"
                  title={e.is_recurring_instance ? `${e.title} (Serie)` : e.title}
                >
                  <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: e.color }} />
                  <span className="truncate">{e.is_recurring_instance ? '🔁 ' : ''}{e.title}</span>
                </div>
              ))}
              {list.length > 3 && (
                <div className="text-[10px] text-muted-foreground">+{list.length - 3} mehr</div>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}

interface NewEventProps {
  defaultDay: Date;
  onClose: () => void;
  onCreated: () => void;
}

/**
 * Erinnerung (Jörg 2026-09-23: „Kalendereinträge sollen wie Todo eine
 * Erinnerung,funktion bekommen"): Chips 15 min · 1 Std. · 1 Tag vorher,
 * mehrfach wählbar; Backend-Feld reminder_minutes_before (Minuten vor Beginn).
 */
const REMINDER_OPTIONS: { minutes: number; label: string }[] = [
  { minutes: 15, label: '15 min vorher' },
  { minutes: 60, label: '1 Std. vorher' },
  { minutes: 1440, label: '1 Tag vorher' },
];

function ReminderChips({ value, onChange, allDay }: { value: number[]; onChange: (v: number[]) => void; allDay: boolean }) {
  const toggle = (m: number): void => {
    onChange(value.includes(m) ? value.filter((x) => x !== m) : [...value, m].sort((a, b) => a - b));
  };
  return (
    <div data-test-anchor="reminder">
      <label className="flex items-center gap-1.5 text-sm text-muted-foreground mb-1.5">
        <Bell size={14} /> Erinnerung (optional)
      </label>
      <div className="flex flex-wrap gap-2">
        {REMINDER_OPTIONS.map((o) => {
          const active = value.includes(o.minutes);
          return (
            <button
              key={o.minutes}
              type="button"
              aria-pressed={active}
              onClick={() => toggle(o.minutes)}
              className={`px-3 py-1.5 text-sm rounded-md border min-h-[36px] ${active
                ? 'bg-primary text-primary-foreground border-primary'
                : 'bg-background text-foreground border-default hover:bg-muted'}`}
            >
              {o.label}
            </button>
          );
        })}
      </div>
      {allDay && value.length > 0 && (
        <div className="text-xs text-muted-foreground mt-1">Bei ganztägigen Terminen bezogen auf die Startzeit.</div>
      )}
    </div>
  );
}

/** Felder eines Termins, wie der Anwender sie tippt (Zeiten als datetime-local). */
interface EventForm {
  title: string;
  startAt: string;
  endAt: string;
  allDay: boolean;
  location: string;
  recurring: boolean;
  rrule: string | null;
  reminders: number[];
}

function validateEvent(v: EventForm): string[] {
  const issues: string[] = [];
  if (!v.title.trim()) issues.push('Titel fehlt');
  if (!v.startAt) issues.push('Start-Zeit fehlt');
  return issues;
}

function eventBody(v: EventForm): Record<string, unknown> {
  return {
    title: v.title.trim(),
    start_at: new Date(v.startAt).toISOString(),
    end_at: v.endAt ? new Date(v.endAt).toISOString() : null,
    all_day: v.allDay,
    location: v.location.trim() || null,
    recurrence_rule: v.recurring ? v.rrule : null,
    reminder_minutes_before: v.reminders,
  };
}

const INPUT = 'w-full h-11 px-3 rounded-md border border-default bg-background text-foreground text-base focus:outline-none focus:ring-2 focus:ring-primary/30';

/** Eingabefelder eines Termins — für Neuanlage und Bearbeitung dieselben. */
function EventFields({ v, setField, fallbackDay }: {
  v: EventForm;
  setField: <K extends keyof EventForm>(key: K, value: EventForm[K]) => void;
  fallbackDay: Date;
}) {
  return (
    <div className="space-y-3">
      <input
        type="text"
        value={v.title}
        onChange={(e) => setField('title', e.target.value)}
        placeholder="Titel"
        autoFocus
        className={INPUT}
        data-test-anchor="event-title"
      />
      <div>
        <label className="text-sm text-muted-foreground mb-1 block">Start</label>
        <input type="datetime-local" value={v.startAt} onChange={(e) => setField('startAt', e.target.value)} className={INPUT} />
      </div>
      <div>
        <label className="text-sm text-muted-foreground mb-1 block">Ende (optional)</label>
        <input type="datetime-local" value={v.endAt} onChange={(e) => setField('endAt', e.target.value)} className={INPUT} />
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={v.allDay} onChange={(e) => setField('allDay', e.target.checked)} />
        ganztägig
      </label>
      <input
        type="text"
        value={v.location}
        onChange={(e) => setField('location', e.target.value)}
        placeholder="Ort (optional)"
        className="w-full h-10 px-3 rounded-md border border-default bg-background text-foreground"
      />
      <label className="flex items-center gap-2 text-sm pt-1 border-t border-default">
        <input type="checkbox" checked={v.recurring} onChange={(e) => setField('recurring', e.target.checked)} />
        <span>🔁 Wiederholt sich</span>
      </label>
      {v.recurring && (
        <RecurrenceEditor
          startDate={v.startAt ? new Date(v.startAt) : fallbackDay}
          value={v.rrule}
          onChange={(r) => setField('rrule', r)}
        />
      )}
      <ReminderChips value={v.reminders} onChange={(r) => setField('reminders', r)} allDay={v.allDay} />
    </div>
  );
}

/** Anzeige eines Termins (Lesemodus des Dokument-Dialogs). */
function EventReadView({ v }: { v: EventForm }) {
  const fmt = (raw: string): string => (raw ? new Date(raw).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: v.allDay ? undefined : 'short' }) : '–');
  const reminderText = v.reminders.length === 0
    ? 'keine'
    : v.reminders.map((m) => REMINDER_OPTIONS.find((o) => o.minutes === m)?.label ?? `${m} min vorher`).join(', ');
  return (
    <dl className="space-y-2 text-sm" data-test-anchor="event-read">
      <div><dt className="text-muted-foreground">Titel</dt><dd className="font-medium text-base">{v.title}</dd></div>
      <div><dt className="text-muted-foreground">Start</dt><dd>{fmt(v.startAt)}{v.allDay ? ' (ganztägig)' : ''}</dd></div>
      <div><dt className="text-muted-foreground">Ende</dt><dd>{v.endAt ? fmt(v.endAt) : '–'}</dd></div>
      <div><dt className="text-muted-foreground">Ort</dt><dd>{v.location || '–'}</dd></div>
      <div><dt className="text-muted-foreground">Wiederholung</dt><dd>{v.recurring && v.rrule ? `🔁 ${v.rrule}` : 'einmalig'}</dd></div>
      <div><dt className="text-muted-foreground">Erinnerung</dt><dd>{reminderText}</dd></div>
    </dl>
  );
}

/**
 * Neuer Termin — Dialog mit Schutz (Abhakliste 2.90): Klick daneben, X und Escape fragen
 * nach, sobald etwas eingegeben ist; Pflichtfelder als Meldung statt Text am Rand.
 */
function NewEventModal({ defaultDay, onClose, onCreated }: NewEventProps) {
  const dlg = useDialogs();
  const [form, setForm] = useState<EventForm>(() => ({
    title: '', startAt: toLocalInput(combineDateTime(defaultDay, 9, 0)), endAt: '', allDay: false,
    location: '', recurring: false, rrule: null, reminders: [],
  }));
  const setField = useCallback(<K extends keyof EventForm>(key: K, value: EventForm[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  }, []);
  const dirty = useDirtySnapshot(true, form);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (): Promise<void> => {
    const issues = validateEvent(form);
    if (issues.length > 0) {
      await dlg.notify({ kind: 'error', title: 'Termin kann nicht angelegt werden', message: 'Bitte zuerst ergänzen:', items: issues });
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/kalender/events', eventBody(form));
      onCreated();
    } catch (err) {
      await dlg.error('Termin wurde nicht angelegt', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <GuardedModal
      open
      title="Neuer Termin"
      label="Termin"
      dirty={dirty}
      busy={submitting}
      onClose={onClose}
      size="sm"
      testAnchor="event-new"
      footer={(requestClose) => (
        <>
          <button type="button" onClick={requestClose} className="px-3 py-2 text-sm text-muted-foreground hover:text-foreground min-h-[44px]">Abbrechen</button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={submitting}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 min-h-[44px]"
            data-action="create"
          >
            {submitting && <Loader2 size={14} className="animate-spin" />} Anlegen
          </button>
        </>
      )}
    >
      <EventFields v={form} setField={setField} fallbackDay={defaultDay} />
    </GuardedModal>
  );
}

/**
 * SD-2026-00034 (Onvice, 2026-07-31): Bearbeiten + Loeschen selbst erstellter
 * Personal-Termine. Laedt via GET /kalender/events/:id (owner_user_id-gefiltert),
 * speichert via PATCH, loescht via DELETE.
 *
 * Dokument-Navigation (Entscheid E5, Abhakliste 2.90): Dialog Art A auf dem Baustein
 * `DocumentDialog` — öffnet in der Anzeige, „Bearbeiten" schaltet um, „Speichern" führt
 * zurück in die Anzeige (Dialog bleibt offen), „Abbrechen" schließt; Klick daneben, X und
 * Escape fragen bei Änderungen nach. Löschen mit Rückfrage im Portal-Layout.
 */
function EditEventModal({ eventId, onClose, onChanged, onDeleted, confirmDelete }: {
  eventId: string;
  onClose: () => void;
  /** Nach erfolgreichem Speichern — Kalender lädt nach, Dialog bleibt in der Anzeige */
  onChanged: () => void;
  onDeleted: () => void;
  confirmDelete: () => Promise<boolean>;
}) {
  const dlg = useDialogs();
  const [baseline, setBaseline] = useState<EventForm | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    let aborted = false;
    (async () => {
      try {
        const r = await api.get<{ success: boolean; event: {
          title: string; start_at: string; end_at: string | null; all_day: boolean;
          location: string | null; recurrence_rule: string | null;
          reminder_minutes_before?: number[] | null;
        } }>(`/kalender/events/${eventId}`);
        if (aborted) return;
        setBaseline({
          title: r.event.title,
          startAt: toLocalInput(new Date(r.event.start_at)),
          endAt: r.event.end_at ? toLocalInput(new Date(r.event.end_at)) : '',
          allDay: !!r.event.all_day,
          location: r.event.location ?? '',
          rrule: r.event.recurrence_rule,
          recurring: !!r.event.recurrence_rule,
          reminders: Array.isArray(r.event.reminder_minutes_before) ? r.event.reminder_minutes_before : [],
        });
      } catch (err) {
        if (aborted) return;
        await dlg.error('Termin konnte nicht geladen werden', err);
        onClose();
      }
    })();
    return () => { aborted = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const doDelete = async (): Promise<void> => {
    if (!(await confirmDelete())) return;
    setDeleting(true);
    try {
      await apiFetch(`/kalender/events/${eventId}`, { method: 'DELETE' });
      onDeleted();
    } catch (err) {
      await dlg.error('Termin wurde nicht gelöscht', err);
      setDeleting(false);
    }
  };

  return (
    <DocumentDialog<EventForm>
      open
      title={baseline?.title ?? 'Termin'}
      label="Termin"
      id={`event:${eventId}`}
      baseline={baseline}
      size="md"
      testAnchor="event-edit"
      validate={validateEvent}
      onSave={async (v) => {
        await apiFetch(`/kalender/events/${eventId}`, { method: 'PATCH', body: JSON.stringify(eventBody(v)) });
        return { ...v, title: v.title.trim(), location: v.location.trim(), rrule: v.recurring ? v.rrule : null };
      }}
      onChanged={onChanged}
      onClose={onClose}
      renderRead={(v) => <EventReadView v={v} />}
      renderEdit={(v, setField) => <EventFields v={v} setField={setField} fallbackDay={new Date()} />}
      readActions={(
        <button
          type="button"
          onClick={() => void doDelete()}
          disabled={deleting}
          className="inline-flex items-center justify-center px-4 py-2 rounded-lg min-h-[44px] text-sm text-danger hover:bg-danger/10 disabled:opacity-50"
          data-action="delete"
        >
          {deleting ? 'Löscht …' : 'Löschen'}
        </button>
      )}
    />
  );
}

function DayEventList({ events, onEventClick, large }: { events: AggregatedEvent[]; onEventClick: (e: AggregatedEvent) => void; large?: boolean }) {
  if (events.length === 0) {
    return <div className="text-sm text-muted-foreground italic">Keine Termine.</div>;
  }
  return (
    <ul className={large ? 'space-y-2' : 'space-y-1.5'}>
      {events.map((e) => (
        <li key={e.id}>
          <button
            type="button"
            onClick={() => onEventClick(e)}
            className={`w-full text-left rounded-md flex items-center gap-3 ${
              large ? 'p-3 border border-default bg-background' : 'px-2 py-1.5'
            } hover:bg-muted ${e.source !== 'holiday' ? 'cursor-pointer' : 'cursor-default'}`}
          >
            <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: e.color }} />
            <div className="flex-1 min-w-0">
              <div className={large ? 'font-medium' : 'font-medium truncate text-sm'}>
                {e.is_recurring_instance && <span title="Wiederkehrender Termin (Serie)" className="mr-1">🔁</span>}
                {e.title}
              </div>
              <div className="text-xs text-muted-foreground">
                {e.all_day ? 'ganztaegig' : formatTime(e.start_at)}
                {e.location ? ` · ${e.location}` : ''}
              </div>
            </div>
          </button>
        </li>
      ))}
    </ul>
  );
}

/**
 * List-View — chronologische Liste aller Events im Range, gruppiert nach Tag.
 * Bei source='task' zusaetzlich Inline-Timer-Button (Play/Stop), der ueber
 * switchTimer den BV2-Timer schaltet. Wenn der User schon einen Timer auf
 * genau dieser Task laufen hat: Stop-Button.
 */
function ListView({ events, activeTaskId, onToggleTimer, onEventClick }: {
  events: AggregatedEvent[];
  activeTaskId: string | null;
  onToggleTimer: (taskId: string) => void;
  onEventClick: (e: AggregatedEvent) => void;
}) {
  // Nach Tag gruppieren (isoDay des start_at). Aggregat kommt bereits sortiert.
  const groups = new Map<string, AggregatedEvent[]>();
  for (const e of events) {
    const key = isoDay(new Date(e.start_at));
    const list = groups.get(key) ?? [];
    list.push(e);
    groups.set(key, list);
  }
  const keys = Array.from(groups.keys()).sort();
  const todayIso = isoDay(new Date());
  return (
    <div className="divide-y divide-default">
      {keys.map((key) => {
        const list = groups.get(key)!;
        const dateObj = new Date(key + 'T00:00:00');
        const isToday = key === todayIso;
        return (
          <div key={key}>
            <div className={`px-4 py-2 text-xs font-medium ${isToday ? 'bg-primary/5 text-primary' : 'bg-muted/30 text-muted-foreground'}`}>
              {dateObj.toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' })}
              {isToday && <span className="ml-2 text-[10px] uppercase tracking-wide">heute</span>}
            </div>
            <ul>
              {list.map((e) => {
                const isTask = e.source === 'task';
                const isActive = isTask && activeTaskId === e.source_id;
                return (
                  <li key={e.id} className="px-4 py-2.5 flex items-center gap-3 hover:bg-muted/30 transition-colors">
                    <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: e.color }} />
                    <button
                      type="button"
                      onClick={() => onEventClick(e)}
                      className="flex-1 min-w-0 text-left"
                    >
                      <div className="text-sm font-medium truncate">
                        {e.is_recurring_instance && <span title="Wiederkehrend" className="mr-1">🔁</span>}
                        {e.title}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {e.all_day ? 'ganztaegig' : formatTime(e.start_at)}
                        {e.location ? ` · ${e.location}` : ''}
                      </div>
                    </button>
                    {isTask && (
                      <button
                        type="button"
                        onClick={() => onToggleTimer(e.source_id)}
                        className={`flex-shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors ${
                          isActive
                            ? 'bg-danger/15 text-danger hover:bg-danger/25'
                            : 'bg-primary/15 text-primary hover:bg-primary/25'
                        }`}
                        title={isActive ? 'Timer stoppen' : 'Timer starten'}
                      >
                        {isActive ? <><Square size={12} /> Stop</> : <><Play size={12} /> Start</>}
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

function firstOfMonth(d: Date): Date {
  const r = new Date(d);
  r.setDate(1);
  r.setHours(0, 0, 0, 0);
  return r;
}
function addMonths(d: Date, n: number): Date {
  const r = new Date(d);
  r.setMonth(r.getMonth() + n);
  return r;
}
function stripTime(d: Date): Date {
  const r = new Date(d);
  r.setHours(0, 0, 0, 0);
  return r;
}
function isoDay(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function formatMonth(d: Date): string {
  return d.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
}
function weekFromAnchor(anchor: Date): Date[] {
  // Liefert die 7 Tage der Woche (Mo–So) in der `anchor` liegt.
  const monday = new Date(anchor);
  const dow = (monday.getDay() + 6) % 7;  // Mo=0 ... So=6
  monday.setDate(monday.getDate() - dow);
  monday.setHours(0, 0, 0, 0);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(d.getDate() + i);
    return d;
  });
}
function formatWeekLabel(week: Date[]): string {
  const a = week[0]!;
  const b = week[6]!;
  return `${a.getDate()}. – ${b.toLocaleDateString('de-DE', { day: '2-digit', month: 'short', year: 'numeric' })}`;
}
function formatDayHeader(d: Date): string {
  return d.toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' });
}
function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}
function combineDateTime(d: Date, h: number, m: number): Date {
  const r = new Date(d);
  r.setHours(h, m, 0, 0);
  return r;
}
function toLocalInput(d: Date): string {
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
